/* One labelled control, with its error where the error happened.
 *
 * LIFTED OUT OF pages/Submit.tsx UNCHANGED. It was written there for the open
 * call and it is the validation idiom of this app: a real `<label>`, the
 * `aria-invalid` / `aria-describedby` wiring done once so no call site can get
 * it wrong, and an error rendered in three redundant signals — the heavier rule,
 * the magenta block, and the words. Never colour alone, and never one red box at
 * the top of a form.
 *
 * The studio needs the same thing on four tabs' worth of forms, and a second
 * dialect of "here is a field and here is what is wrong with it" would be two
 * places to get accessibility wrong. So it lives here now and both pages import
 * it.
 *
 * WHY `children` IS A FUNCTION. The wiring — the id, `aria-invalid`,
 * `aria-describedby` — belongs on the input, not on this wrapper, and the
 * wrapper is the only thing that knows the error and hint ids. Handing the props
 * down makes the correct spelling the easy one; a `<Field><input/></Field>` that
 * cloned its child would silently drop the wiring the moment somebody wrapped
 * the input in a div.
 *
 * TWO CLASS NAMES ON THE HINT AND THE SWATCH, and that is deliberate rather than
 * sloppy. `sub__hint` / `sub__errswatch` are styled inside Submit.tsx's own
 * page-scoped `<style>` block, which only exists while that page is mounted;
 * `fld__hint` / `fld__errswatch` are what every other page styles. Carrying both
 * keeps the submissions form pixel-identical to what it was before the lift
 * while giving the studio something to hang a rule on.
 */

import type { ReactNode } from 'react';

export interface FieldProps {
  /**
   * The log-sheet number, e.g. `01`. Optional: the open call numbers its rows
   * because it is a form somebody fills in once, top to bottom, and the studio
   * does not because it is a desk somebody edits one field of.
   */
  index?: string;
  label: string;
  required?: boolean;
  id: string;
  error?: string;
  hint?: string;
  /**
   * Announce the error as it appears. OFF by default, and that default is not
   * laziness: the open call already carries one `role="alert"` summary above the
   * form, so announcing every field as well would read a four-error submission
   * out five times. The studio has no such summary and its field errors appear
   * on blur, while focus is already moving away, so it opts in.
   */
  announce?: boolean;
  /** The input itself, handed the wiring it must not get wrong. */
  children: (props: {
    id: string;
    'aria-invalid'?: 'true';
    'aria-describedby'?: string;
  }) => ReactNode;
}

/**
 * One numbered row of the log sheet.
 *
 * The error markup is not optional decoration: base.css drops the input's left
 * border when `data-error` is set, so the magenta block and the input read as
 * one object rather than two things that happen to touch.
 */
export function Field({
  index,
  label,
  required = false,
  id,
  error,
  hint,
  announce = false,
  children,
}: FieldProps) {
  const errId = `${id}-err`;
  const hintId = `${id}-hint`;
  const described = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ');

  const input = children({
    id,
    'aria-invalid': error ? 'true' : undefined,
    'aria-describedby': described || undefined,
  });

  return (
    <div className="fld" data-error={error ? 'true' : undefined}>
      <label htmlFor={id}>
        {index ? (
          <>
            {index} &nbsp;
          </>
        ) : null}
        {label}{' '}
        {required ? <span className="dim">— required</span> : <span className="dim">— optional</span>}
      </label>
      {error ? (
        <div className="fld__errwrap">
          <div className="fld__errblk" aria-hidden="true" />
          {input}
        </div>
      ) : (
        input
      )}
      {hint ? (
        <p className="mono dim fld__hint sub__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      <FieldError id={errId} message={error} announce={announce} />
    </div>
  );
}

/**
 * The third signal: the words.
 *
 * The rule weight and the magenta block are the other two, and neither of them
 * survives being read aloud. This one does, which is why it is the one that is
 * never optional.
 */
export function FieldError({
  id,
  message,
  announce = false,
}: {
  id: string;
  message?: string;
  announce?: boolean;
}) {
  if (!message) return null;
  return (
    <p className="fld__err" id={id} role={announce ? 'alert' : undefined}>
      <span className="fld__errswatch sub__errswatch" aria-hidden="true" />
      <span>{message}</span>
    </p>
  );
}
